import React from 'react';

import { RangeCalendarButton } from '@components/Calendar';
import { PlaceAutocomplete } from '@components/PlaceAutocomplete';
import {
  DateInputWithError,
  EmailInputWithError,
  PasswordInputWithError,
  TextInputRef,
  TextInputWithError,
  TextInputWithErrorProps,
} from '@components/TextInput';
import { useSelector } from '@tanstack/react-store';
import { Place } from '@types';
import { DateRange } from '@types';

import { useFieldContext } from './context';
import { getErrorText } from './errors';

export type StringFieldProps = Omit<
  TextInputWithErrorProps,
  'error' | 'errorText' | 'onBlur' | 'onChangeText' | 'value'
> & {
  /** Forwards focus control to screens that chain keyboard submit actions. */
  inputRef?: React.Ref<TextInputRef>;
  /** Normalizes user-entered text before committing it to form state. */
  transformValue?: (value: string) => string;
};

type BaseStringInputProps = StringFieldProps & {
  InputComponent: typeof TextInputWithError;
};

function BaseStringInput({
  InputComponent,
  inputRef,
  transformValue,
  ...props
}: BaseStringInputProps) {
  const field = useFieldContext<string>();
  const submissionAttempts = useSelector(field.form.store, (state) => state.submissionAttempts);
  const shouldShowError = field.state.meta.isBlurred || submissionAttempts > 0;
  const errorText = shouldShowError ? getErrorText(field.state.meta.errors) : '';

  return (
    <InputComponent
      ref={inputRef}
      error={Boolean(errorText)}
      errorText={errorText}
      onBlur={field.handleBlur}
      onChangeText={(text) => field.handleChange(transformValue ? transformValue(text) : text)}
      value={field.state.value ?? ''}
      {...props}
    />
  );
}

export function FormTextInput(props: StringFieldProps) {
  return <BaseStringInput InputComponent={TextInputWithError} {...props} />;
}

export function FormEmailInput(props: StringFieldProps) {
  return <BaseStringInput InputComponent={EmailInputWithError} {...props} />;
}

export function FormPasswordInput(props: StringFieldProps) {
  return <BaseStringInput InputComponent={PasswordInputWithError} {...props} />;
}

export function FormDateInput({ inputRef: _inputRef, transformValue, ...props }: StringFieldProps) {
  const field = useFieldContext<string>();
  const submissionAttempts = useSelector(field.form.store, (state) => state.submissionAttempts);
  const shouldShowError = field.state.meta.isBlurred || submissionAttempts > 0;
  const errorText = shouldShowError ? getErrorText(field.state.meta.errors) : '';

  return (
    <DateInputWithError
      defaultValue={field.state.value ?? ''}
      error={Boolean(errorText)}
      errorText={errorText}
      onBlur={field.handleBlur}
      onChangeText={(text) => field.handleChange(transformValue ? transformValue(text) : text)}
      {...props}
    />
  );
}

export type PlaceFieldProps = Omit<
  React.ComponentProps<typeof PlaceAutocomplete>,
  'error' | 'errorText' | 'initialPlace' | 'onBlur' | 'onSelect' | 'value'
> & {
  /** Forwards focus control to screens that chain keyboard submit actions. */
  inputRef?: React.Ref<TextInputRef>;
};

export function FormPlaceAutocomplete({ inputRef, ...props }: PlaceFieldProps) {
  const field = useFieldContext<Place | undefined>();
  const submissionAttempts = useSelector(field.form.store, (state) => state.submissionAttempts);
  const shouldShowError = field.state.meta.isBlurred || submissionAttempts > 0;
  const errorText = shouldShowError ? getErrorText(field.state.meta.errors) : '';

  return (
    <PlaceAutocomplete
      ref={inputRef}
      error={Boolean(errorText)}
      errorText={errorText}
      initialPlace={field.state.value}
      onBlur={field.handleBlur}
      onSelect={field.handleChange}
      {...props}
    />
  );
}

export type DateRangeFieldProps = Omit<
  React.ComponentProps<typeof RangeCalendarButton>,
  'errorText' | 'initialDate' | 'onSelect' | 'range'
>;

export function FormDateRangeInput(props: DateRangeFieldProps) {
  const field = useFieldContext<DateRange>();
  const submissionAttempts = useSelector(field.form.store, (state) => state.submissionAttempts);
  const shouldShowError = field.state.meta.isBlurred || submissionAttempts > 0;
  const errorText = shouldShowError ? getErrorText(field.state.meta.errors) : '';
  const range = field.state.value ?? { startDate: undefined, endDate: undefined };

  return (
    <RangeCalendarButton
      errorText={errorText}
      initialDate={range.endDate}
      onSelect={(nextRange) => {
        if (!nextRange) {
          return;
        }
        field.handleChange({
          startDate: nextRange.startDate,
          endDate: nextRange.endDate,
        });
      }}
      range={range}
      {...props}
    />
  );
}
